import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchBrands } from "@/lib/api";

const TICKER_ITEMS = [
    "Just Eat", "Deliveroo", "Uber Eats", "Panda", "Walk In Welcome",
    "Detroit Pizza", "Peri Peri", "Ramen & Maki", "Chinese", "Fish & Chips",
    "Edinburgh-Made", "Indian Curries", "Burgers & Wings", "Shawarma", "Halal",
    "Desserts", "Parathas",
];

const Landing = () => {
    const [brandCount, setBrandCount] = useState(14);

    useEffect(() => {
        fetchBrands().then((b) => setBrandCount(b.length)).catch(() => {});
    }, []);

    return (
        <div className="bg-[var(--bg)] text-[var(--text)] min-h-screen" data-testid="landing-page">
            {/* Top nav */}
            <header className="border-b border-[var(--border)]">
                <div className="container-nk py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="font-display text-xl tracking-tight">naami<span className="text-gold">.</span></div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hidden sm:block">
                            Order Hub
                        </span>
                    </Link>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hidden md:block">
                        1 Saltire Sq · Edinburgh · EH5 1PR
                    </span>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden" style={{ minHeight: "78vh" }}>
                <div className="absolute inset-0 hero-grid opacity-60" aria-hidden />
                <div className="absolute inset-0 hero-glow" aria-hidden />
                <div className="container-nk relative z-10 pt-24 pb-20">
                    <p className="eyebrow fade-up" style={{ animationDelay: "0.05s" }} data-testid="hero-eyebrow">
                        // The Ordering Ecosystem
                    </p>
                    <h1
                        className="font-display fade-up mt-5 leading-[0.92]"
                        style={{ fontSize: "clamp(56px, 11vw, 132px)", animationDelay: "0.15s" }}
                        data-testid="hero-headline"
                    >
                        Twenty Brands.<br />
                        <span className="text-gold">One Hub.</span>
                    </h1>
                    <p
                        className="fade-up mt-7 max-w-[640px] text-[17px] leading-relaxed text-[var(--text)]/80"
                        style={{ animationDelay: "0.28s" }}
                    >
                        Four ordering surfaces, one kitchen. Walk up to the kiosk, scan a brand QR at
                        the wall, or send a slip straight from the central hub behind the pass.
                    </p>
                    <div className="fade-up flex flex-wrap gap-4 mt-10" style={{ animationDelay: "0.42s" }}>
                        <Link to="/kiosk" className="btn-primary" data-testid="hero-cta-kiosk">
                            Open Kiosk <span aria-hidden>→</span>
                        </Link>
                        <Link to="/counter" className="btn-secondary" data-testid="hero-cta-counter">
                            Counter POS
                        </Link>
                        <Link to="/brands" className="btn-ghost" data-testid="hero-cta-brands">
                            Browse QR Menus
                        </Link>
                    </div>

                    {/* Corner badge */}
                    <div className="hidden lg:flex absolute top-24 right-12 flex-col items-center justify-center w-[150px] h-[150px] rounded-full border border-[var(--border)] bg-[var(--surface)]/40 backdrop-blur-sm fade-up" style={{ animationDelay: "0.55s" }}>
                        <span className="font-display text-[60px] text-gold leading-none tabular-nums" data-testid="hero-brand-count">{brandCount}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2 text-center">Brands<br />Under One Roof</span>
                    </div>
                </div>
            </section>

            {/* Ticker */}
            <div className="ticker" data-testid="ticker">
                <div className="ticker-track py-3">
                    {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
                        <div className="ticker-item" key={i}>
                            {t}
                            <span className="ticker-dot" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Surface grid */}
            <section className="py-20 lg:py-28">
                <div className="container-nk">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <p className="eyebrow">// Pick a Surface</p>
                            <h2 className="font-display text-[44px] md:text-[64px] leading-[0.95] mt-3">
                                Four Ways to Order.<br />
                                <span className="text-gold">One System Behind.</span>
                            </h2>
                        </div>
                        <p className="text-muted text-[15px] max-w-md font-light leading-relaxed">
                            Whichever way an order comes in, it lands on the same live queue at the pass —
                            split, routed and ready to fire.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SurfaceCard
                            testId="surface-kiosk"
                            num="01"
                            eyebrow="// Walk In"
                            title="Self-Serve Kiosk"
                            desc="Multi-brand. One cart. One swipe at the floor terminal."
                            to="/kiosk"
                        />
                        <SurfaceCard
                            testId="surface-hub"
                            num="02"
                            eyebrow="// Staff"
                            title="Central Order Hub"
                            desc="Live queue. Status routing. Audible alerts. Print slips."
                            to="/hub/login"
                        />
                        <SurfaceCard
                            testId="surface-qr"
                            num="03"
                            eyebrow="// Scan"
                            title="QR Brand Menus"
                            desc="Per-brand mobile menus. Scan a code at the wall, order from your phone."
                            to="/brands"
                        />
                        <SurfaceCard
                            testId="surface-print"
                            num="04"
                            eyebrow="// Pass"
                            title="Split Print Slips"
                            desc="Auto-generated per brand. 80mm thermal-friendly. Launched from the Hub."
                            to="/hub"
                        />
                    </div>
                </div>
            </section>

            {/* About strip */}
            <section className="py-16 border-t border-[var(--border)] bg-[var(--surface)]">
                <div className="container-nk grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="eyebrow">// The Hub at 1 Saltire Square</p>
                        <h3 className="font-display text-[40px] md:text-[56px] leading-[0.95] mt-3">
                            Edinburgh's ghost kitchen, <span className="text-gold">behind the counter</span>.
                        </h3>
                    </div>
                    <div className="flex flex-col gap-5 pt-2">
                        <p className="text-[15.5px] leading-[1.75] text-[var(--text)]/80 font-light">
                            This ordering ecosystem powers walk-in, QR and kitchen-side operations
                            across every brand under the Naami roof. Mix dishes from many kitchens in
                            one order — collect it minutes later at the same counter.
                        </p>
                        <div className="flex flex-col gap-1 mt-2">
                            <div className="stat-box">
                                <span className="font-display text-[44px] text-gold leading-none">{brandCount}</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">Brands Live Now</span>
                            </div>
                            <div className="stat-box">
                                <span className="font-display text-[44px] text-gold leading-none">04</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">Ordering Surfaces</span>
                            </div>
                            <div className="stat-box">
                                <span className="font-display text-[44px] text-gold leading-none">07</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">Days a Week</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[var(--border)] py-8">
                <div className="container-nk flex flex-wrap items-center justify-between gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                        © Naami Kitchens · Edinburgh
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                        Staff demo PIN · <span className="text-gold">4321</span>
                    </span>
                </div>
            </footer>
        </div>
    );
};

const SurfaceCard = ({ to, num, eyebrow, title, desc, testId }) => (
    <Link
        to={to}
        data-testid={testId}
        className="step-card group block hover:border-[var(--gold)] transition-colors"
    >
        <span className="step-num">{num}</span>
        <span className="eyebrow relative z-10">{eyebrow}</span>
        <h3 className="font-display text-[30px] md:text-[36px] mt-4 leading-tight relative z-10 group-hover:text-gold transition-colors">
            {title}
        </h3>
        <p className="text-[14px] text-[var(--text)]/70 mt-3 font-light leading-relaxed relative z-10 max-w-md">
            {desc}
        </p>
        <div className="mt-7 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold relative z-10">
            Open <span aria-hidden>→</span>
        </div>
    </Link>
);

export default Landing;
